import { cn } from '../../utils/cn'
import { ReactNode, useState } from 'react'
import { Info } from 'lucide-react'

interface TooltipProps {
  content: string
  children: ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
}

const positionClasses = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
}

const arrowClasses = {
  top: 'top-full left-1/2 -translate-x-1/2 border-t-slate-800 dark:border-t-slate-700 border-x-transparent border-b-transparent',
  bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-slate-800 dark:border-b-slate-700 border-x-transparent border-t-transparent',
  left: 'left-full top-1/2 -translate-y-1/2 border-l-slate-800 dark:border-l-slate-700 border-y-transparent border-r-transparent',
  right: 'right-full top-1/2 -translate-y-1/2 border-r-slate-800 dark:border-r-slate-700 border-y-transparent border-l-transparent',
}

export function Tooltip({
  content,
  children,
  position = 'top',
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)

  return (
    <div
      className="relative inline-flex items-center gap-1"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      <Info className="w-4 h-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-help" />

      {/* Tooltip content */}
      <div
        className={cn(
          'absolute z-50 px-3 py-2 rounded-lg',
          'bg-slate-800 dark:bg-slate-700 text-white text-sm',
          'whitespace-nowrap max-w-xs',
          'transition-all duration-200',
          isVisible ? 'opacity-100 visible' : 'opacity-0 invisible',
          positionClasses[position]
        )}
      >
        {content}
        {/* Arrow */}
        <div
          className={cn(
            'absolute w-0 h-0',
            'border-[6px] border-solid',
            arrowClasses[position]
          )}
        />
      </div>
    </div>
  )
}
